import axios from 'axios';

// Base URL from environment (points to Gateway)
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Get available agents for current user to assign
 * @returns {Promise<Array>} List of available tenant agents
 */
export const getAvailableAgents = async () => {
    const token = localStorage.getItem('token');

    const response = await axios.get(`${API_BASE_URL}asrosync/user-agents/available`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    return response.data;
};

/**
 * Get current user's assigned chat agent
 * @returns {Promise<Object>} Current agent assignment
 */
export const getCurrentAgent = async () => {
    const token = localStorage.getItem('token');

    const response = await axios.get(`${API_BASE_URL}asrosync/user-agents/current`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    return response.data;
};

/**
 * Assign an agent to current user
 * @param {number} tenantAgentId - ID of the tenant agent to assign
 * @returns {Promise<Object>} Assignment result
 */
export const assignAgent = async (tenantAgentId) => {
    const token = localStorage.getItem('token');

    const response = await axios.post(
        `${API_BASE_URL}asrosync/user-agents/assign`,
        { tenantAgentId },
        {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        }
    );

    return response.data;
};

/**
 * Remove current agent assignment (will use system default)
 * @returns {Promise<Object>} Result
 */
export const removeAgentAssignment = async () => {
    const token = localStorage.getItem('token');

    const response = await axios.delete(`${API_BASE_URL}asrosync/user-agents/current`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    return response.data;
};

// ---------------------------------------------------------------------------
// Agent prompts (UI-stored prompts for LangChain agent_system_prompt & tool_knowledge_base_lookup)
// Backend: GET/PUT /synctrack/agent-prompt/{promptType}
// ---------------------------------------------------------------------------

const getPromptBaseUrl = () => {
    const base = (process.env.REACT_APP_BACKEND_URL).replace(/\/$/, '');
    return `${base}/synctrack`;
};

/**
 * Get a single agent prompt by type (and optional tenant + agent for per-agent prompts).
 * @param {string} promptType - e.g. "agent_system_prompt", "tool_knowledge_base_lookup"
 * @param {string|null} tenantId - Optional tenant ID
 * @param {string|null} agentName - Optional agent name for per-agent prompts (tenant schema)
 * @returns {Promise<{ found: boolean, prompt_text?: string, ... }|null>}
 */
export const getAgentPrompt = async (promptType, tenantId = null, agentName = null) => {
    const token = localStorage.getItem('token');
    const params = {};
    if (tenantId) params.tenantId = tenantId;
    if (agentName) params.agentName = agentName;
    const response = await axios.get(`${getPromptBaseUrl()}/agent-prompt/${promptType}`, {
        params: Object.keys(params).length ? params : undefined,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    }).catch((err) => {
        if (err.response?.status === 404) return { data: { found: false } };
        throw err;
    });
    return response?.data ?? null;
};

/**
 * Save (create or update) an agent prompt.
 * @param {string} promptType - e.g. "agent_system_prompt", "tool_knowledge_base_lookup"
 * @param {object} body - { prompt_text: string, description?: string, tenantId?: string, agentName?: string }
 * @returns {Promise<object>}
 */
export const saveAgentPrompt = async (promptType, body) => {
    const token = localStorage.getItem('token');
    const response = await axios.put(`${getPromptBaseUrl()}/agent-prompt/${promptType}`, body, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    return response.data;
};
